import { Popover } from "antd";
import { type FC, useCallback, useMemo, useState } from "react";
import Checkbox from "../../Checkbox/Checkbox";
import { Icon } from "../../Icons";
import { LoadingSpinner } from "../../LoadingSpinner/LoadingSpinner";
import { Tag } from "../../Tag";
import { Body } from "../../Typography/Body";

export interface AppointmentFilterOption {
  id: string;
  label: string;
  isCurrent: boolean;
  disabled?: boolean;
}

interface AppointmentFilterDropdownProps {
  options: AppointmentFilterOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** When true, shows a spinner on the trigger — signals that other
   *  appointments' briefings are being fetched in the background. */
  loading?: boolean;
}

export const AppointmentFilterDropdown: FC<AppointmentFilterDropdownProps> = ({
  options,
  selectedIds,
  onChange,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const enabledOptions = useMemo(
    () => options.filter((o) => !o.disabled),
    [options],
  );
  const allEnabledSelected = enabledOptions.every((o) => selectedSet.has(o.id));

  const handleToggle = useCallback(
    (id: string, checked: boolean) => {
      const next = checked
        ? [...selectedIds, id]
        : selectedIds.filter((v) => v !== id);
      onChange(next);
    },
    [selectedIds, onChange],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        onChange(enabledOptions.map((o) => o.id));
      } else {
        onChange([]);
      }
    },
    [enabledOptions, onChange],
  );

  const triggerLabel = useMemo(() => {
    const count = selectedIds.length;
    if (count === 0) return "No appointments";
    const current = options.find((o) => o.isCurrent);
    if (count === 1 && current && selectedIds[0] === current.id) {
      return "Current appointment";
    }
    return `${count} appointments`;
  }, [selectedIds, options]);

  const content = (
    <div className="indigo-appointment-filter__menu">
      <div className="indigo-appointment-filter__select-all">
        <Checkbox
          checked={allEnabledSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          <Body size="sm" className="font-medium">
            Select all {options.length} appointments
          </Body>
        </Checkbox>
      </div>
      <div className="indigo-appointment-filter__divider" />
      {options.map((opt) => (
        <div
          key={opt.id}
          className={`indigo-appointment-filter__option ${opt.disabled ? "indigo-appointment-filter__option--disabled" : ""}`}
        >
          <Checkbox
            checked={selectedSet.has(opt.id)}
            disabled={opt.disabled}
            onChange={(e) => handleToggle(opt.id, e.target.checked)}
          >
            <Body size="sm" className="font-medium">
              {opt.label}
            </Body>
          </Checkbox>
          {opt.isCurrent && (
            <Tag
              label="Current"
              variant="filled"
              status="info"
              shape="pill"
              size="small"
            />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      overlayClassName="indigo-appointment-filter__popover"
      arrow={false}
    >
      {/* Trigger is a div+role=button (not <button>) so the inner clear
          button is valid HTML — a <button> inside <button> is spec-invalid
          and browsers promote the inner one out, breaking keyboard
          behaviour. */}
      <div
        role="button"
        tabIndex={0}
        className="indigo-appointment-filter__trigger"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
      >
        <span className="indigo-appointment-filter__trigger-label">
          {triggerLabel}
        </span>
        {loading ? (
          <LoadingSpinner size={12} ariaLabel="Loading appointment briefings" />
        ) : (
          <button
            type="button"
            aria-label="Clear appointment filter"
            className="indigo-appointment-filter__trigger-clear"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
          >
            ×
          </button>
        )}
        <Icon
          name="down-chevron"
          size="xs"
          className="indigo-appointment-filter__trigger-arrow"
        />
      </div>
    </Popover>
  );
};
